// T6-BOARD-NOTE (universal-task plan §b.3, §b.4): the board settings tab "How it works", next to
// Fields. What it shows comes from `GET boards/{id}/note`:
//   - the owner (a DE runs the board, or a person) and how often a DE owner checks it;
//   - the note, "How this board works" (a versioned page the owner writes; a person edits it in
//     a modal, saved as a new version);
//   - the rules that run on the board, each as its sentence, rendered from the rules themselves
//     (never retyped), with its firing log, inline edit (the rule builder), on/off, and
//     "Pause rules" for all of them at once.
// Every action is a visible hover icon; explanations sit behind (i).

import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  NativeSelect,
  NativeSelectOption,
  Textarea,
} from "@companyos/ui";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  HistoryIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  PowerIcon,
  PowerOffIcon,
} from "lucide-react";
import { type FormEvent, Fragment, useId, useState } from "react";
import type { Api } from "../../../api/client";
import {
  processKeys,
  type Rule,
  rulesQuery,
  runsQuery,
} from "../../automations/automation-queries";
import { type BuilderStart, RuleBuilder } from "../../automations/rule-builder";
import { problemText } from "../../settings/problem-text";
import { ago } from "../doc-kinds";
import type { Board } from "../view-engine";
import { invalidateWork, workKeys } from "../work-queries";
import { OwnerPicker } from "./owner-picker";
import { type BoardSettingsTabProps, registerBoardSettingsTab } from "./registry";

export type BoardNote = OperationOutput<"boards.note">;
type NoteRule = BoardNote["rules"][number];

export const noteKey = (co: string, board: string) =>
  [...workKeys.board(co, board), "note"] as const;

export const noteQuery = (api: Api, co: string, board: string) =>
  queryOptions({
    queryKey: noteKey(co, board),
    queryFn: () => api.client.boards.note({ params: { co, id: board } }),
  });

/** The check windows offered (minutes). */
export const WINDOWS = [
  { value: 5, label: "5 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "hour" },
  { value: 240, label: "4 hours" },
  { value: 1440, label: "day" },
] as const;

/** The note's markdown as light blocks: headings, bullets, paragraphs (no HTML is trusted). */
export function NoteBody({ markdown }: { markdown: string }) {
  const lines = markdown.split("\n");
  return (
    <div data-slot="board-note" className="flex flex-col gap-1 text-sm leading-relaxed">
      {lines.map((line, i) => {
        const key = `${i}:${line}`;
        const h = /^(#{1,3})\s+(.*)$/.exec(line);
        if (h)
          return (
            <p key={key} className="mt-2 font-semibold text-foreground first:mt-0">
              {h[2]}
            </p>
          );
        const li = /^\s*(?:[-*+]|\d+[.)])\s+(?:\[[ xX]\]\s+)?(.*)$/.exec(line);
        if (li)
          return (
            <p key={key} className="pl-4 -indent-3">
              • {li[1]}
            </p>
          );
        if (line.trim() === "") return null;
        return <p key={key}>{line.replace(/^>\s?/, "")}</p>;
      })}
    </div>
  );
}

export function HowItWorksTab({ board, can, api, co, company }: BoardSettingsTabProps) {
  if (!api || !co) return null;
  return <HowItWorks api={api} co={co} board={board} edit={can.edit} company={company ?? null} />;
}

function HowItWorks({
  api,
  co,
  board,
  edit,
  company,
}: {
  api: Api;
  co: string;
  board: Board;
  edit: boolean;
  company: BoardSettingsTabProps["company"] | null;
}) {
  const qc = useQueryClient();
  const note = useQuery(noteQuery(api, co, board.id));
  const rules = useQuery({ ...rulesQuery(api, co), enabled: Boolean(company) });
  const [writing, setWriting] = useState(false);
  const [builder, setBuilder] = useState<BuilderStart | null>(null);
  const [log, setLog] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const ids = { window: useId() };
  const n = note.data;
  const editable = edit && (n?.can_edit ?? false);
  const refresh = async () => {
    // the note and the board itself (a first note moves the board's version: Codex r1 #13)
    await invalidateWork(qc, co);
    await qc.invalidateQueries({ queryKey: processKeys.rules(co) });
  };
  const act = async (what: string, fn: () => Promise<unknown>) => {
    setPending(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(problemText(e, what));
    } finally {
      setPending(false);
    }
  };
  const setWindow = (minutes: number) =>
    act("The check window could not be saved.", async () => {
      await api.client.boards.update({
        params: { co, id: board.id },
        ifMatch: board.version,
        body: { settings: { ...board.settings, wake_window_minutes: minutes } },
      });
      await invalidateWork(qc, co);
    });
  const fullRule = (r: NoteRule): Rule | undefined => rules.data?.data.find((x) => x.id === r.id);

  if (note.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (note.isError || !n)
    return (
      <p role="alert" className="text-sm text-danger-text">
        {problemText(note.error, "How this board works could not be loaded.")}
      </p>
    );
  const ownerIsDe = n.owner?.holder === "de";
  return (
    <div data-tab="how" className="flex flex-col gap-5 text-sm">
      <dl className="m-0 grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-3">
        <dt className="flex items-center gap-1 text-muted-foreground">
          Owner
          <InfoTip label="About the owner">
            A DE owner runs the board: it reads what happens on it, moves the work and keeps this
            note true. A person owner runs it by hand.
          </InfoTip>
        </dt>
        <dd className="m-0 min-w-0">
          <OwnerPicker api={api} co={co} board={board} editable={editable} />
        </dd>
        <dt className="flex items-center gap-1 text-muted-foreground">
          <label htmlFor={ids.window}>Checks every</label>
          <InfoTip label="About the check window">
            How often a DE owner looks at what changed on the board. A comment or a person's move
            wakes it at once.
          </InfoTip>
        </dt>
        <dd className="m-0 flex items-center gap-2">
          <NativeSelect
            id={ids.window}
            size="sm"
            disabled={!editable || pending}
            value={String(n.wake_window_minutes)}
            onChange={(e) => void setWindow(Number(e.target.value))}
          >
            {WINDOWS.map((w) => (
              <NativeSelectOption key={w.value} value={String(w.value)}>
                {w.label}
              </NativeSelectOption>
            ))}
            {WINDOWS.some((w) => w.value === n.wake_window_minutes) ? null : (
              <NativeSelectOption value={String(n.wake_window_minutes)}>
                {n.wake_window_minutes} minutes
              </NativeSelectOption>
            )}
          </NativeSelect>
          {n.owner && !ownerIsDe ? <Badge variant="neutral">Only for a DE owner</Badge> : null}
        </dd>
      </dl>

      <section aria-labelledby="how-note" className="group/note flex flex-col gap-2">
        <header className="flex min-h-7 items-center gap-1">
          <h3 id="how-note" className="m-0 text-sm font-semibold">
            Note
          </h3>
          <InfoTip label="About the note">
            What the board is for, its stages, fields, who approves what and what people do by hand.
            The owner keeps it; every save is a version.
          </InfoTip>
          {n.version ? (
            <span className="text-xs text-muted-foreground">
              v{n.version}
              {n.updated_at ? ` · ${ago(n.updated_at)}` : ""}
            </span>
          ) : null}
          {editable ? (
            <Button
              variant="ghost"
              size="icon-xs"
              className="ml-auto opacity-0 group-hover/note:opacity-100 group-focus-within/note:opacity-100 pointer-coarse:opacity-100"
              aria-label={n.version ? "Edit the note" : "Write the note"}
              title={n.version ? "Edit the note" : "Write the note"}
              onClick={() => setWriting(true)}
            >
              <PencilIcon aria-hidden="true" />
            </Button>
          ) : null}
        </header>
        {n.body_markdown ? (
          <div className="rounded-md border border-subtle px-3 py-2">
            <NoteBody markdown={n.body_markdown} />
          </div>
        ) : (
          <p className="text-muted-foreground">
            {ownerIsDe ? "No note yet: the owner writes it." : "No note yet."}
          </p>
        )}
      </section>

      <section aria-labelledby="how-rules" className="flex flex-col gap-2">
        <header className="flex min-h-7 items-center gap-1">
          <h3 id="how-rules" className="m-0 text-sm font-semibold">
            Rules
          </h3>
          <InfoTip label="About the rules">
            Fixed moves that run by themselves (a date reached, a number crossed, a status entered).
            Anything that needs reading goes to the owner instead.
          </InfoTip>
          {n.rules_paused ? <Badge variant="warning">Paused</Badge> : null}
          <span className="ml-auto flex items-center">
            {editable && n.rules.length ? (
              <Button
                variant="ghost"
                size="xs"
                disabled={pending}
                onClick={() =>
                  void act("The rules could not be paused.", () =>
                    api.client.boards.pauseRules({
                      params: { co, id: board.id },
                      body: { paused: !n.rules_paused },
                    }),
                  )
                }
              >
                {n.rules_paused ? (
                  <PlayIcon aria-hidden="true" />
                ) : (
                  <PauseIcon aria-hidden="true" />
                )}
                {n.rules_paused ? "Resume rules" : "Pause rules"}
              </Button>
            ) : null}
            {editable && company ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Add a rule"
                title="Add a rule"
                onClick={() => setBuilder({ kind: "new", boardId: board.id })}
              >
                <PlusIcon aria-hidden="true" />
              </Button>
            ) : null}
          </span>
        </header>
        {n.rules.length ? (
          <ul aria-label="Rules" className="m-0 flex list-none flex-col gap-1 p-0">
            {n.rules.map((r) => {
              const full = fullRule(r);
              const on = r.enabled && !r.paused_reason;
              return (
                <Fragment key={r.id}>
                  <li
                    data-rule={r.id}
                    className="group/rule flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-muted"
                  >
                    <div className="min-w-0 flex-1">
                      <p className={cn("m-0", !on && "text-muted-foreground")}>{r.sentence}</p>
                      <p className="m-0 text-xs text-muted-foreground">
                        {r.owner_de_name ? (
                          <>
                            by {r.owner_de_name}{" "}
                            <Badge variant="neutral" className="align-middle">
                              DE
                            </Badge>
                            {" · "}
                          </>
                        ) : null}
                        {!r.enabled ? "Off · " : r.paused_reason ? "Paused · " : ""}
                        {r.fired_count
                          ? `Ran ${r.fired_count}×${r.last_fired_at ? `, last ${ago(r.last_fired_at)}` : ""}`
                          : "Never ran"}
                      </p>
                    </div>
                    <span className="flex shrink-0 items-center opacity-0 group-hover/rule:opacity-100 group-focus-within/rule:opacity-100 pointer-coarse:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Firing log of ${r.name}`}
                        title="Firing log"
                        aria-expanded={log === r.id}
                        onClick={() => setLog(log === r.id ? null : r.id)}
                      >
                        <HistoryIcon aria-hidden="true" />
                      </Button>
                      {editable && r.can_edit && full && company ? (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={`Edit ${r.name}`}
                          title="Edit"
                          onClick={() => setBuilder({ kind: "edit", rule: full })}
                        >
                          <PencilIcon aria-hidden="true" />
                        </Button>
                      ) : null}
                      {editable && r.can_edit ? (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={on ? `Switch ${r.name} off` : `Switch ${r.name} on`}
                          title={on ? "Switch off" : "Switch on"}
                          disabled={pending}
                          onClick={() =>
                            void act("The rule could not be switched.", () =>
                              on
                                ? api.client.automations.disable({ params: { co, id: r.id } })
                                : api.client.automations.enable({ params: { co, id: r.id } }),
                            )
                          }
                        >
                          {on ? (
                            <PowerOffIcon aria-hidden="true" />
                          ) : (
                            <PowerIcon aria-hidden="true" />
                          )}
                        </Button>
                      ) : null}
                    </span>
                  </li>
                  {log === r.id ? <FiringLog api={api} co={co} ruleId={r.id} /> : null}
                </Fragment>
              );
            })}
          </ul>
        ) : (
          <p className="text-muted-foreground">No rules: the owner moves everything.</p>
        )}
      </section>
      {error ? (
        <p role="alert" className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
      {writing ? (
        <NoteDialog
          api={api}
          co={co}
          note={n}
          onClose={() => setWriting(false)}
          onSaved={refresh}
        />
      ) : null}
      {builder && company ? (
        <RuleBuilder
          api={api}
          company={company}
          start={builder}
          onClose={() => {
            setBuilder(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

const OUTCOME: Record<string, string> = {
  fired: "Ran",
  partial: "Ran in part",
  refused: "Refused",
  skipped: "Skipped",
};

/** A rule's last firings, newest first (what it did, or why not). */
function FiringLog({ api, co, ruleId }: { api: Api; co: string; ruleId: string }) {
  const runs = useQuery(runsQuery(api, co, ruleId));
  const rows = runs.data?.data ?? [];
  return (
    <li data-slot="firing-log" className="ml-2 border-l border-subtle pl-3 text-xs">
      {runs.isPending ? (
        <p className="m-0 text-muted-foreground">Loading…</p>
      ) : rows.length ? (
        <ul aria-label="Firing log" className="m-0 flex list-none flex-col gap-0.5 p-0">
          {rows.slice(0, 20).map((x) => (
            <li key={x.id} className="flex gap-2">
              <span className="w-16 shrink-0 text-muted-foreground">{ago(x.created_at)}</span>
              <span>
                {OUTCOME[x.outcome] ?? x.outcome}
                {x.effect_count
                  ? ` · ${x.effect_count} change${x.effect_count === 1 ? "" : "s"}`
                  : ""}
                {x.refused_reason ? ` · ${x.refused_reason}` : ""}
                {x.acted_as_name ? ` · by ${x.acted_as_name}${x.acted_as_de ? " (DE)" : ""}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-muted-foreground">It has not run yet.</p>
      )}
    </li>
  );
}

/** Edit the note (markdown) in a modal; saved as the note's next version. */
function NoteDialog({
  api,
  co,
  note,
  onClose,
  onSaved,
}: {
  api: Api;
  co: string;
  note: BoardNote;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const id = useId();
  const [text, setText] = useState(note.body_markdown);
  // the version this draft is based on, kept when the note refetches (Codex r1 #12)
  const [base] = useState(note.version);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await api.client.boards.writeNote({
        params: { co, id: note.board_id },
        body: { body_markdown: text, version: base },
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(problemText(err, "The note could not be saved."));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form className="grid gap-4" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>How {note.board_name} works</DialogTitle>
            <DialogDescription>Markdown. Saved as a new version.</DialogDescription>
          </DialogHeader>
          <Textarea
            id={id}
            aria-label="Note"
            className="min-h-64 font-mono text-[13px]"
            maxLength={10_000}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {error ? (
            <p role="alert" className="text-xs text-danger-text">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="submit" disabled={pending || text === note.body_markdown}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const off = registerBoardSettingsTab({
  id: "how",
  title: "How it works",
  order: 15,
  Component: HowItWorksTab,
  when: (p) => Boolean(p.api && p.co),
});
// A hot reload re-runs this module: the old registration goes first (no duplicate id).
import.meta.hot?.dispose(off);
